import React, { useEffect, useState } from "react";

import styled, { css } from "styled-components";

import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";

const SIDEBAR_BASE_WIDTH = 360;
const SIDEBAR_MIN_WIDTH = 260;
const SIDEBAR_MAX_WIDTH = 1000;
const SIDEBAR_AUTOFOLD_WIDTH = 300;
const SIDEBAR_DATA_KEY = "reviewSidebarData";
const LEFT_SIDEBAR_BASE_WIDTH = 300;
const LEFT_SIDEBAR_MIN_WIDTH = 220;
const LEFT_SIDEBAR_MAX_WIDTH = 520;
const LEFT_SIDEBAR_AUTOFOLD_WIDTH = 260;
const RAIL_WIDTH = 40;

const MainRow = styled.div`
    display: flex;
    flex: 1;
    min-height: 0;
    height: 100%;
    width: 100%;
    position: relative;
`;

const LeftSidebar = styled.aside`
    position: relative;
    width: ${(props) => (props.$isFold ? "0px" : props.$width + "px")};
    height: 100%;
    background-color: var(--color-White);
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    overflow: hidden;
    transition: ${({ $isResizing }) => ($isResizing ? "none" : "width 180ms ease-in-out")};
`;

const MainSection = styled.main`
    flex: 1;
    height: 100%;
    position: relative;
    overflow: auto;
`;

const Sidebar = styled.aside`
    position: relative;
    width: ${(props) => (props.$isFold ? "0px" : props.$width + "px")};
    max-width: ${SIDEBAR_MAX_WIDTH}px;
    height: 100%;
    background-color: var(--color-White);
    transition: ${({ $isResizing }) => ($isResizing ? "none" : "width 180ms ease-in-out")};
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const PanelRail = styled.aside`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: ${RAIL_WIDTH}px;
    height: 100%;
    padding: 8px 4px;
    flex-shrink: 0;
    background: ${(props) => (props.$side === "right" ? "var(--color-White)" : "var(--color-Base1)")};
    border-right: ${(props) => (props.$side === "left" ? "1px solid var(--color-Line)" : "0")};
    border-left: ${(props) => (props.$side === "right" ? "1px solid var(--color-Line)" : "0")};
`;

const RailButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid ${(props) => (props.$active ? "var(--color-Key)" : "transparent")};
    border-radius: 8px;
    background: ${(props) => (props.$active ? "color-mix(in srgb, var(--color-Key) 12%, white)" : "transparent")};
    cursor: pointer;

    &:hover {
        background: ${(props) =>
            props.$active ? "color-mix(in srgb, var(--color-Key) 12%, white)" : "var(--color-ButtonHover4)"};
    }
`;

const ResizeHandle = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3px;
    flex-shrink: 0;
    background-color: var(--color-Grey3);
    opacity: 0.5;
    border: 0;
    cursor: ew-resize;
    transition:
        background-color 160ms ease-in,
        opacity 160ms ease-in;

    &:hover {
        background-color: var(--color-Key);
        opacity: ${({ $hidden }) => ($hidden ? 0.5 : 1)};
    }

    ${(props) =>
        props.$hidden &&
        css`
            cursor: pointer;
        `}
`;

const ResizeHandleToggle = styled.button`
    position: absolute;
    top: calc(50% - 25px);
    left: 0;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 50px;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: var(--color-Grey1);
    color: var(--color-White);
    cursor: pointer !important;
    font-size: 8px;
    font-weight: 800;
    line-height: 1;

    &:hover {
        background: var(--color-NormalBlack);
    }
`;

function ReferenceDetailPageLayout({
    main,
    leftSidebar = null,
    sidebar,
    sidebarKey = SIDEBAR_DATA_KEY,
    sidebarBaseWidth = SIDEBAR_BASE_WIDTH,
    sidebarMaxWidth = SIDEBAR_MAX_WIDTH,
    leftSidebarBaseWidth = LEFT_SIDEBAR_BASE_WIDTH,
    leftSidebarMaxWidth = LEFT_SIDEBAR_MAX_WIDTH,
    showPanelRails = false,
    showRightPanelRail = showPanelRails,
}) {
    const [sidebarWidth, setSidebarWidth] = useState(sidebarBaseWidth);
    const [leftSidebarWidth, setLeftSidebarWidth] = useState(leftSidebarBaseWidth);
    const [isLeftSidebarFold, setIsLeftSidebarFold] = useState(false);
    const [isSidebarFold, setIsSidebarFold] = useState(false);
    const [isSidebarResizing, setIsSidebarResizing] = useState(false);
    const [isLeftSidebarResizing, setIsLeftSidebarResizing] = useState(false);
    const [suppressLeftToggleClick, setSuppressLeftToggleClick] = useState(false);

    useEffect(() => {
        // fold/width localStorage 저장/불러오기
        const sidebarData = JSON.parse(localStorage.getItem(sidebarKey));
        if (sidebarData) {
            setIsSidebarFold(sidebarData.f || false);
            setSidebarWidth(Math.max(SIDEBAR_MIN_WIDTH, sidebarData.w || sidebarBaseWidth));
            setIsLeftSidebarFold(sidebarData.lf || false);
            setLeftSidebarWidth(Math.max(LEFT_SIDEBAR_MIN_WIDTH, sidebarData.lw || leftSidebarBaseWidth));
        }
    }, [leftSidebarBaseWidth, sidebarKey, sidebarBaseWidth]);

    useEffect(() => {
        localStorage.setItem(
            sidebarKey,
            JSON.stringify({
                w: sidebarWidth,
                f: isSidebarFold,
                lw: leftSidebarWidth,
                lf: isLeftSidebarFold,
            }),
        );
    }, [isLeftSidebarFold, isSidebarFold, leftSidebarWidth, sidebarKey, sidebarWidth]);

    const startLeftSidebarResizing = (event) => {
        if (isLeftSidebarFold) return;
        event.preventDefault();
        const startX = event.clientX;
        const startWidth = leftSidebarWidth;
        let didResize = false;
        let latestWidth = leftSidebarWidth;
        setIsLeftSidebarResizing(true);
        document.body.style.userSelect = "none";
        document.body.style.cursor = "col-resize";

        const handleMouseMove = (e) => {
            const nextWidth = Math.max(
                LEFT_SIDEBAR_MIN_WIDTH,
                Math.min(leftSidebarMaxWidth, startWidth + e.clientX - startX),
            );
            if (Math.abs(e.clientX - startX) > 2) didResize = true;
            latestWidth = nextWidth;
            setLeftSidebarWidth(nextWidth);
        };

        const handleMouseUp = () => {
            setIsLeftSidebarResizing(false);
            setSuppressLeftToggleClick(didResize);
            if (latestWidth <= LEFT_SIDEBAR_AUTOFOLD_WIDTH) {
                setIsLeftSidebarFold(true);
                setLeftSidebarWidth(LEFT_SIDEBAR_MIN_WIDTH);
            }
            document.body.style.userSelect = "";
            document.body.style.cursor = "";
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
    };

    const startSidebarResizing = (event) => {
        if (isSidebarFold) return;
        event.preventDefault();
        const startX = event.clientX;
        const startWidth = sidebarWidth;
        let latestWidth = sidebarWidth;
        setIsSidebarResizing(true);
        document.body.style.userSelect = "none";
        document.body.style.cursor = "col-resize";

        const handleMouseMove = (e) => {
            const nextWidth = Math.max(SIDEBAR_MIN_WIDTH, Math.min(sidebarMaxWidth, startWidth + startX - e.clientX));
            latestWidth = nextWidth;
            setSidebarWidth(nextWidth);
        };

        const handleMouseUp = () => {
            setIsSidebarResizing(false);
            if (latestWidth <= SIDEBAR_AUTOFOLD_WIDTH) {
                setIsSidebarFold(true);
                setSidebarWidth(SIDEBAR_MIN_WIDTH);
            }
            document.body.style.userSelect = "";
            document.body.style.cursor = "";
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
    };

    const handleResizeToggleClick = (event, togglePanelCollapsed, suppressClick, setSuppressClick) => {
        event.stopPropagation();
        if (suppressClick) {
            setSuppressClick(false);
            return;
        }
        togglePanelCollapsed();
    };

    return (
        <MainRow>
            {showPanelRails && leftSidebar && (
                <PanelRail $side="left">
                    <Tooltip message={isLeftSidebarFold ? "리뷰 목록 열기" : "리뷰 목록 닫기"} position="right">
                        <RailButton
                            type="button"
                            aria-label="리뷰 목록"
                            $active={!isLeftSidebarFold}
                            onClick={() => setIsLeftSidebarFold((prev) => !prev)}
                        >
                            <Icon
                                name={"thread"}
                                size={"16px"}
                                color={!isLeftSidebarFold ? "var(--color-Key)" : "var(--color-Grey1)"}
                            />
                        </RailButton>
                    </Tooltip>
                </PanelRail>
            )}
            {leftSidebar && (
                <>
                    <LeftSidebar
                        $width={leftSidebarWidth}
                        $isFold={isLeftSidebarFold}
                        $isResizing={isLeftSidebarResizing}
                    >
                        {leftSidebar}
                    </LeftSidebar>
                    {showPanelRails && (
                        <ResizeHandle
                            onMouseDown={isLeftSidebarFold ? undefined : startLeftSidebarResizing}
                            $hidden={isLeftSidebarFold}
                            onClick={isLeftSidebarFold ? () => setIsLeftSidebarFold(false) : undefined}
                        >
                            <ResizeHandleToggle
                                type="button"
                                onClick={(event) =>
                                    handleResizeToggleClick(
                                        event,
                                        () => setIsLeftSidebarFold((prev) => !prev),
                                        suppressLeftToggleClick,
                                        setSuppressLeftToggleClick,
                                    )
                                }
                                aria-label={isLeftSidebarFold ? "좌측 패널 열기" : "좌측 패널 닫기"}
                            >
                                {isLeftSidebarFold ? ">" : "<"}
                            </ResizeHandleToggle>
                        </ResizeHandle>
                    )}
                </>
            )}
            <MainSection>{main}</MainSection>
            {sidebar && showPanelRails && !isSidebarFold && (
                <ResizeHandle onMouseDown={startSidebarResizing} $hidden={false} />
            )}
            {sidebar && (
                <Sidebar $width={sidebarWidth} $isResizing={isSidebarResizing} $isFold={isSidebarFold}>
                    {React.cloneElement(sidebar, { isSidebarFold, setIsSidebarFold })}
                </Sidebar>
            )}
            {sidebar && showRightPanelRail && (
                <PanelRail $side="right">
                    <Tooltip message={isSidebarFold ? "리뷰 패널 열기" : "리뷰 패널 닫기"} position="left">
                        <RailButton
                            type="button"
                            aria-label={isSidebarFold ? "리뷰 패널 열기" : "리뷰 패널 닫기"}
                            $active={false}
                            onClick={() => setIsSidebarFold((prev) => !prev)}
                        >
                            <Icon
                                name={isSidebarFold ? "arrowLeft" : "arrowRight"}
                                size={"16px"}
                                color={"var(--color-Grey1)"}
                            />
                        </RailButton>
                    </Tooltip>
                </PanelRail>
            )}
        </MainRow>
    );
}

export default ReferenceDetailPageLayout;
